import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingBag, User, Menu, X, Heart, Package, Search, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';

const NAV_CATEGORIES = [
  { to: '/shop?sort=newest', label: 'New In' },
  { to: '/shop?category=vintage', label: 'Vintage' },
  { to: '/shop?category=streetwear', label: 'Streetwear' },
  { to: '/shop?category=jackets', label: 'Jackets' },
  { to: '/shop?category=tshirts', label: 'T-Shirts' },
];

const MOBILE_SECTIONS = [
  {
    title: 'Shop',
    links: [
      { to: '/shop?sort=newest', label: 'New In', accent: true },
      { to: '/shop?category=vintage', label: 'Vintage' },
      { to: '/shop?category=streetwear', label: 'Streetwear' },
      { to: '/shop?category=jackets', label: 'Jackets' },
      { to: '/shop?category=tshirts', label: 'T-Shirts' },
      { to: '/shop', label: 'View All' },
    ],
  },
  {
    title: 'Help & Info',
    links: [
      { to: '/track-order', label: 'Track My Order' },
      { to: '/about', label: 'About Us' },
      { to: '/contact', label: 'Contact' },
      { to: '/faq', label: 'FAQ' },
      { to: '/sizing-guide', label: 'Sizing Guide' },
    ],
  },
];

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { cartCount, openDrawer } = useCart();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const [announcementIndex, setAnnouncementIndex] = useState(0);
  const announcements = [
    'Free UK shipping on orders over \u00A350 \u2014 shop now',
    'New drops every week \u2014 follow us to stay ahead',
    'One-off pieces, never restocked \u2014 don\u0027t wait',
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setAnnouncementIndex(prev => (prev + 1) % 3);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const closeMenu = () => setMobileMenuOpen(false);

  const handleCartClick = (e) => {
    e.preventDefault();
    openDrawer();
  };

  return (
    <>
      {/* Announcement Bar — rotating */}
      <div className="bg-[#0A0A0A] text-white text-center py-2.5 text-[11px] tracking-[0.15em] uppercase font-medium overflow-hidden" data-testid="announcement-bar">
        <span key={announcementIndex} className="inline-block animate-fadeIn">{announcements[announcementIndex]}</span>
      </div>
      <nav
        className={`sticky top-0 z-50 border-b transition-all duration-300 ${
          scrolled
            ? 'bg-[#F9F8F6]/95 backdrop-blur-md shadow-[0_2px_16px_rgba(0,0,0,0.06)] border-[#E2E2E2]/80'
            : 'bg-[#F9F8F6] border-[#E2E2E2] shadow-none'
        }`}
        data-testid="main-navbar"
      >
        <div className="container-custom">
          <div className="flex items-center justify-between h-[72px] md:h-[80px]">

            {/* Mobile: hamburger left */}
            <button
              className="h-10 w-10 flex items-center justify-center md:hidden -ml-1 btn-icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-5 w-5 text-[#0A0A0A]" /> : <Menu className="h-5 w-5 text-[#0A0A0A]" />}
            </button>

            {/* Logo — centered on mobile, left on desktop */}
            <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo-link" onClick={closeMenu}>
              <div className="relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_654bd78e-5e39-4822-a480-29d8c14fa990/artifacts/ow0h2qv9_Black%20and%20White%20Minimalist%20Round%20Beauty%20And%20Spa%20Logo.png"
                  alt="Vintage Wardrobe Co"
                  className="relative h-12 md:h-14 w-12 md:w-14 rounded-full object-cover bg-white"
                  width={56}
                  height={56}
                  data-testid="nav-logo-image"
                />
              </div>
            </Link>

            {/* Desktop Nav — categories centered */}
            <div className="hidden md:flex items-center gap-8 lg:gap-10">
              {NAV_CATEGORIES.map(({ to, label }) => (
                <Link
                  key={to + label}
                  to={to}
                  className="relative text-[11px] font-semibold uppercase tracking-[0.12em] text-[#0A0A0A] hover:text-[#C9A84C] transition-colors duration-200 py-1 group"
                  data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}-link`}
                >
                  {label}
                  <span className="absolute bottom-0 left-0 w-0 h-[1.5px] bg-[#C9A84C] group-hover:w-full transition-all duration-300" />
                </Link>
              ))}
              <Link
                to="/shop"
                className="relative text-[11px] font-semibold uppercase tracking-[0.12em] text-[#666] hover:text-[#C9A84C] transition-colors duration-200 py-1 group"
                data-testid="nav-all-link"
              >
                All
                <span className="absolute bottom-0 left-0 w-0 h-[1.5px] bg-[#C9A84C] group-hover:w-full transition-all duration-300" />
              </Link>
            </div>

            {/* Right icons */}
            <div className="flex items-center gap-1">
              {/* Desktop-only extras */}
              <div className="hidden md:flex items-center gap-1">
                {user ? (
                  <>
                    {user.is_admin && (
                      <Link to={`/${localStorage.getItem('admin_path') || 'admin'}`} data-testid="nav-admin-link">
                        <button className="h-9 px-3 text-[10px] font-semibold uppercase tracking-[0.1em] text-[#666] hover:text-[#C9A84C] transition-colors duration-200 btn-icon">
                          Admin
                        </button>
                      </Link>
                    )}
                    <Link to="/my-orders" data-testid="nav-orders-link">
                      <button className="h-9 w-9 flex items-center justify-center rounded-full hover:bg-[#F0EEEB] transition-colors duration-200 group btn-icon" data-testid="nav-orders-button">
                        <Package className="h-[18px] w-[18px] text-[#0A0A0A] group-hover:text-[#C9A84C] transition-colors" strokeWidth={1.5} />
                      </button>
                    </Link>
                    <Link to="/wishlist" data-testid="nav-wishlist-link">
                      <button className="h-9 w-9 flex items-center justify-center rounded-full hover:bg-[#F0EEEB] transition-colors duration-200 group btn-icon" data-testid="nav-wishlist-button">
                        <Heart className="h-[18px] w-[18px] text-[#0A0A0A] group-hover:text-[#C9A84C] transition-colors" strokeWidth={1.5} />
                      </button>
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="h-9 px-3 text-[10px] font-semibold uppercase tracking-[0.1em] text-[#666] hover:text-[#C9A84C] transition-colors duration-200 btn-icon"
                      data-testid="nav-logout-button"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <Link to="/login" data-testid="nav-login-link">
                    <button className="h-9 w-9 flex items-center justify-center rounded-full hover:bg-[#F0EEEB] transition-colors duration-200 group btn-icon" data-testid="nav-login-button">
                      <User className="h-[18px] w-[18px] text-[#0A0A0A] group-hover:text-[#C9A84C] transition-colors" strokeWidth={1.5} />
                    </button>
                  </Link>
                )}
              </div>

              {/* Cart — always visible, opens drawer */}
              <button
                onClick={handleCartClick}
                className="relative h-9 w-9 flex items-center justify-center rounded-full hover:bg-[#F0EEEB] transition-colors duration-200 group btn-icon"
                data-testid="nav-cart-button"
              >
                <ShoppingBag className="h-[18px] w-[18px] text-[#0A0A0A] group-hover:text-[#C9A84C] transition-colors" strokeWidth={1.5} />
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-[#C9A84C] text-white text-[8px] font-bold rounded-full w-[17px] h-[17px] flex items-center justify-center" data-testid="cart-count-badge">
                    {cartCount > 9 ? '9+' : cartCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu — full-screen overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[60] md:hidden" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-black/30" onClick={closeMenu} />
          <div className="absolute top-0 left-0 bottom-0 w-[85%] max-w-[340px] bg-[#F9F8F6] shadow-2xl overflow-y-auto animate-slideRight">
            {/* Header */}
            <div className="flex items-center justify-between h-[60px] px-5 border-b border-[#E2E2E2]">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0A0A0A]">Menu</span>
              <button onClick={closeMenu} className="w-9 h-9 flex items-center justify-center btn-icon">
                <X className="w-5 h-5 text-[#0A0A0A]" />
              </button>
            </div>

            {/* Search */}
            <div className="px-5 py-4">
              <div
                className="flex items-center gap-2 h-12 px-3 bg-white border border-[#E2E2E2] rounded-lg cursor-pointer"
                onClick={() => { closeMenu(); navigate('/shop'); }}
                data-testid="mobile-search-bar"
              >
                <Search className="w-4 h-4 text-[#666]" />
                <span className="text-sm text-[#666]">Search products...</span>
              </div>
            </div>

            {/* Sections */}
            <div className="px-5 space-y-5 pb-8">
              {MOBILE_SECTIONS.map((section) => (
                <div key={section.title}>
                  <p className="text-[9px] font-semibold uppercase tracking-[0.25em] text-[#666] mb-2">{section.title}</p>
                  <div className="space-y-0.5">
                    {section.links.map(({ to, label, accent }) => (
                      <Link
                        key={to + label}
                        to={to}
                        onClick={closeMenu}
                        className={`flex items-center justify-between py-3 px-1 rounded-md hover:bg-[#F0EEEB] transition-colors ${
                          accent ? 'text-[#C9A84C] font-bold' : 'text-[#0A0A0A]'
                        }`}
                        style={{ minHeight: '56px' }}
                        data-testid={`mobile-nav-${label.toLowerCase().replace(/\s/g, '-')}-link`}
                      >
                        <span className="text-sm font-medium">{label}</span>
                        <ChevronRight className="w-4 h-4 text-[#E2E2E2]" />
                      </Link>
                    ))}
                  </div>
                </div>
              ))}

              {/* Account Section */}
              <div className="border-t border-[#E2E2E2] pt-4">
                <p className="text-[9px] font-semibold uppercase tracking-[0.25em] text-[#666] mb-2">Account</p>
                <div className="space-y-0.5">
                  {user ? (
                    <>
                      <Link to="/my-orders" onClick={closeMenu} className="flex items-center justify-between py-3 px-1 rounded-md hover:bg-[#F0EEEB] transition-colors" style={{ minHeight: '56px' }} data-testid="mobile-nav-my-orders-link">
                        <span className="text-sm font-medium text-[#0A0A0A]">My Orders</span>
                        <ChevronRight className="w-4 h-4 text-[#E2E2E2]" />
                      </Link>
                      <Link to="/wishlist" onClick={closeMenu} className="flex items-center justify-between py-3 px-1 rounded-md hover:bg-[#F0EEEB] transition-colors" style={{ minHeight: '56px' }} data-testid="mobile-nav-wishlist-link">
                        <span className="text-sm font-medium text-[#0A0A0A]">Wishlist</span>
                        <ChevronRight className="w-4 h-4 text-[#E2E2E2]" />
                      </Link>
                      {user.is_admin && (
                        <Link to={`/${localStorage.getItem('admin_path') || 'admin'}`} onClick={closeMenu} className="flex items-center justify-between py-3 px-1 rounded-md hover:bg-[#F0EEEB] transition-colors" style={{ minHeight: '56px' }} data-testid="mobile-nav-admin-link">
                          <span className="text-sm font-medium text-[#0A0A0A]">Admin</span>
                          <ChevronRight className="w-4 h-4 text-[#E2E2E2]" />
                        </Link>
                      )}
                      <button onClick={() => { handleLogout(); closeMenu(); }} className="flex items-center justify-between py-3 px-1 rounded-md hover:bg-[#F0EEEB] transition-colors w-full text-left" style={{ minHeight: '56px' }} data-testid="mobile-nav-logout-button">
                        <span className="text-sm font-medium text-[#666]">Logout</span>
                      </button>
                    </>
                  ) : (
                    <Link to="/login" onClick={closeMenu} className="flex items-center justify-between py-3 px-1 rounded-md hover:bg-[#F0EEEB] transition-colors" style={{ minHeight: '56px' }} data-testid="mobile-nav-login-link">
                      <span className="text-sm font-medium text-[#0A0A0A]">Login / Register</span>
                      <ChevronRight className="w-4 h-4 text-[#E2E2E2]" />
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
